import { isValidElement, useEffect, useState, type ReactNode } from "react";
import { CollapsibleSection } from "@/components/ui/detail-card";
import type { Box } from "@/components/ui/box.schema";
import type { VisitSection } from "@/lib/visit/registry";

function notices(box?: Box): string[] {
  if (!box) return [];
  const readNotices = box.read.status === "failed" || box.read.status === "stale" ? [box.read.sentence] : [];
  const withRead = (...sentences: string[]) => [...readNotices, ...sentences];
  if (box.kind === "task") {
    const result = box.result;
    if (result.state === "unknown") return withRead(`Not confirmed. ${result.sentence}`);
    if (result.state === "refused") return withRead(result.sentence);
    if (result.state === "partial") return withRead(result.cap || "Some work still needs review.", ...result.failed.map(line => line.sentence));
    if (box.parts.some(part => part.conflict)) return withRead("A newer version needs review.");
    if (box.draft) return withRead("Unfinished changes are kept here.");
  }
  if (box.kind === "fact" && box.fields && (box.read.status === "ready")) {
    return box.fields(box.read.value).flatMap(field => field.conflict ? [`${field.label}: a newer version needs review.`]
      : field.save.status === "unknown" ? [`${field.label}: save not confirmed. Open to check.`]
      : field.save.status === "failed" || field.save.status === "refused" ? [`${field.label}: ${field.save.sentence}`]
      : field.save.status === "saving" ? [`${field.label}: saving.`] : []);
  }
  return readNotices;
}

function Section({ section, ids, available, requested }: { section: VisitSection; ids: string[]; available: Map<string | undefined, ReactNode>; requested: boolean }) {
  const [open, setOpen] = useState(Boolean(section.defaultOpen || requested));
  // Async reads can resolve the explicitly addressed work after entry.
  useEffect(() => { if (requested || section.defaultOpen) setOpen(true); }, [requested, section.defaultOpen]);
  const attention = [...new Set(ids.flatMap(id => {
    const node = available.get(id);
    return isValidElement<{ box?: Box }>(node) ? notices(node.props.box) : [];
  }))];
  const summary = !open && (section.summary || attention.length) ? <div className="space-y-1">
    {section.summary && <p>{section.summary}</p>}
    {attention.length > 0 && <p role="status" className="text-status-warning-foreground">{attention.join(" ")} Open {section.label.toLowerCase()} to review.</p>}
  </div> : undefined;
  return <div data-record-section={section.id}><CollapsibleSection icon={section.icon} label={section.label} summary={summary} open={open} onOpenChange={setOpen}>
    {ids.map(id => available.get(id))}
  </CollapsibleSection></div>;
}

/** Ordered presentation only. Unlisted boxes and original native nodes survive. */
export function VisitSections({ sections, nodes, opens = [] }: { sections: VisitSection[]; nodes: ReactNode[]; opens?: string[] }) {
  const idOf = (node: ReactNode) => isValidElement<{ box?: Box; "data-box-id"?: string }>(node) ? node.props.box?.id ?? node.props["data-box-id"] : undefined;
  const available = new Map(nodes.map((node) => [idOf(node), node]));
  const sectionIds = new Set(sections.flatMap(section => section.boxIds));
  const firstSection = nodes.findIndex(node => sectionIds.has(idOf(node) ?? ""));
  const leading = nodes.slice(0, firstSection < 0 ? nodes.length : firstSection);
  const grouped = new Set<string>();
  return <div className="flex min-w-0 flex-col gap-3" data-record-sections="">
    {leading}
    {sections.map((section) => {
      const ids = section.boxIds.filter((id) => available.has(id) && !grouped.has(id));
      ids.forEach((id) => grouped.add(id));
      if (!ids.length) return null;
      return <Section key={section.id} section={section} ids={ids} available={available} requested={ids.some(id => opens.includes(id))} />;
    })}
    {nodes.filter((node) => !leading.includes(node) && !grouped.has(idOf(node) ?? ""))}
  </div>;
}
